import { fmtINR } from "@/lib/format";

// Conditional visibility: cond = { field_id, op(equals|not_equals|contains), value }.
export function fieldVisible(field, values) {
  const cond = field.conditional;
  if (!cond || !cond.field_id) return true;
  const target = values[cond.field_id];
  const op = cond.op || "equals";
  if (op === "equals") return String(target) === String(cond.value);
  if (op === "not_equals") return String(target) !== String(cond.value);
  if (op === "contains") return Array.isArray(target) ? target.includes(cond.value) : String(target || "").includes(String(cond.value));
  return true;
}

const inp = "h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";

// Renders a dynamic Deal/Qualification form from a versioned template's fields.
export default function DealFormFields({ fields = [], values = {}, onChange, idPrefix = "deal" }) {
  const set = (id, v) => onChange(id, v);
  const toggle = (id, opt) => {
    const cur = Array.isArray(values[id]) ? values[id] : [];
    set(id, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  return (
    <div className="space-y-4">
      {fields.filter((f) => fieldVisible(f, values)).map((f) => {
        const id = f.field_id;
        const tid = `${idPrefix}-field-${id}`;
        if (f.field_type === "section") {
          return <div key={id} className="pt-1"><p className="text-xs font-semibold uppercase tracking-wide text-ink/50">{f.label}</p></div>;
        }
        const label = (
          <label className="mb-1 block text-xs font-medium text-ink/60">{f.label}{f.required && <span className="text-red-400"> *</span>}</label>
        );
        if (f.field_type === "checkbox") {
          const arr = Array.isArray(values[id]) ? values[id] : [];
          return (
            <div key={id}>
              {label}
              <div className="flex flex-wrap gap-1.5" data-testid={tid}>
                {(f.options || []).map((o) => {
                  const on = arr.includes(o.value);
                  return (
                    <button type="button" key={o.value} onClick={() => toggle(id, o.value)}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-loop bg-loop/10 font-medium text-loop" : "border-sky text-ink/60 hover:bg-mist"}`}>
                      {o.label || o.value}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        if (f.field_type === "radio" || f.field_type === "dropdown") {
          return (
            <div key={id}>
              {label}
              <select className={inp} data-testid={tid} value={values[id] || ""} onChange={(e) => set(id, e.target.value)}>
                <option value="">Select…</option>
                {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label || o.value}</option>)}
              </select>
            </div>
          );
        }
        if (f.field_type === "longtext") {
          return <div key={id}>{label}<textarea className="w-full rounded-lg border border-sky p-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" rows={2} data-testid={tid} value={values[id] || ""} onChange={(e) => set(id, e.target.value)} /></div>;
        }
        const type = f.field_type === "date" ? "date" : (f.field_type === "number" || f.field_type === "currency") ? "number" : f.field_type === "url" ? "url" : "text";
        return (
          <div key={id}>
            {label}
            <div className="relative">
              {f.field_type === "currency" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink/40">₹</span>}
              <input type={type} className={`${inp} ${f.field_type === "currency" ? "pl-7" : ""}`} data-testid={tid}
                value={values[id] ?? ""} onChange={(e) => set(id, type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
            </div>
            {f.field_type === "currency" && values[id] ? <p className="mt-0.5 text-[11px] text-ink/40">{fmtINR(values[id])}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

// Returns list of missing required field labels (respecting visibility).
export function missingRequired(fields = [], values = {}) {
  return fields.filter((f) => f.field_type !== "section" && f.required && fieldVisible(f, values))
    .filter((f) => { const v = values[f.field_id]; return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0); })
    .map((f) => f.label);
}
